import useModal from '@area2k/use-modal';
import { faClock } from '@fortawesome/free-regular-svg-icons';
import { faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';
import { BarsArrowDownIcon, BarsArrowUpIcon } from '@heroicons/react/20/solid';
import { differenceInHours, parseISO } from 'date-fns';
import { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';

import { getTaxTypeLabel } from '../Order/util';

import DetailsCard from './DetailsCard';
import JobBadge from './JobBadge';
import JobHeader from './JobHeader';

import SurgeBanner from '@/routes/Agency/PaySurge/components/SurgeBanner';
import JobActivityTab from './ActivityTab';
import { ActivityOrder } from '@/routes/Agency/DashboardV2/api/types';
import PageSkeleton from './PageSkeleton';
import WorkerTable from './WorkerTable';
import CancellationDissmissModal from './modals/CancellationAndDismissModal';
import ConfirmModalCancellation from './modals/ConfirmModalCancellation';
import { getJobStatusByJob, JobStatus } from './util';

import Alert from '@/components/Alert';
import Badge from '@/components/Badge';
import Button from '@/components/Button';
import Page from '@/components/Page';
import Stack from '@/components/Stack';
import Tabs, { TabDefinition } from '@/components/Tabs';
import { GAEvent } from '@/constants/gaevents';
import { TIME_TO_REFRESH } from '@/constants/general';
import {
  ContextCancelRemoveModal,
  ContextConfirmModalCancellation,
} from '@/constants/job';
import {
  useGetJobQuery,
  useUnPublishJobMutation,
  usePublishJobMutation,
} from '@/graphql';
import useAuth from '@/hooks/useAuth';
import useNullableMediaQuery from '@/hooks/useNullableMediaQuery';
import styled from '@/styles';
import { GetJobQuery, JobTypeEnum } from '@/types/graphql';
import { JobsAPI, JobQualityGateData, JobSupplyInfo } from '@/services/jobsAPI';
import { PoolRow, PreferredPoolsAPI } from '@/services/preferredPoolsAPI';
import useAnalytics from '@/util/analytics';
import { DAY_HOURS } from '@/util/constants';
import { formatISO, isWithin24Hours } from '@/util/date';
import { getHiredWorkersByJobWorkers } from '@/util/job';
import AgencyChat from '@/routes/Agency/Chat';
import { FEATURE_TOGGLE } from '@/constants/featuretoggle';
import { useFeatureValue } from '@growthbook/growthbook-react';
import { isIOS } from '@/util/platform';

const JobDetailsWrapper = styled('div', {
  display: 'flex',
  flexFlow: 'row wrap',
  justifyContent: 'flex-start',
  gap: '50px',
  padding: '20px',
  '@phoneOnly': {
    justifyContent: 'center',
    gap: 0,
    padding: 0,
  },
});
const JobRevamp = () => {
  const { jobId, orderId } = useParams();
  // GRAVY-10453. `?shift=<id>` names WHICH occurrence of this job to show.
  //
  // The page is otherwise single-occurrence by construction — the worker rows
  // matched a timesheet on the worker alone and anchored edits to `shifts[0]` —
  // so a multi-shift job showed an arbitrary occurrence's hours with nothing on
  // screen saying which. The Worker Profile's "Review shift" link carries the
  // flagged shift id for exactly this reason: ops should not have to work out
  // which day the flag was about.
  const [searchParams] = useSearchParams();
  const requestedShiftId = searchParams.get('shift');
  // GRAVY-10453 round 3. `?worker=<id>` names WHOSE row the deep link is about.
  //
  // `?shift=` narrowed ops to the right occurrence, but the occurrence still
  // lists every hired worker — and with two same-named workers on it ("John
  // Doe" twice) nothing said which row carried the flag, so ops opened each in
  // turn. Highlighting the row by worker ID rather than by name is the point:
  // the names are exactly what cannot be told apart.
  const requestedWorkerId = searchParams.get('worker');
  const {
    currentAdminIsCustomerAdmin,
    currentAdminIsTenantAdminRole,
    currentAgency,
  } = useAuth();
  const query = useGetJobQuery({
    variables: { jobId },
    pollInterval: TIME_TO_REFRESH * 2,
  });
  const [tab, setTab] = useState(0);
  // The Activity tab's sort toggle (GRAVY-10877) — newest first, as the design
  // and the dashboard panel both open.
  const [activityOrder, setActivityOrder] = useState<ActivityOrder>('desc');
  const [qualityGate, setQualityGate] = useState<JobQualityGateData>({
    quality_gate_disabled: false,
    quality_gate_disabled_reason: null,
    quality_gate_disabled_at: null,
    customer_quality_gate_enabled: false,
  });
  const [supplyInfo, setSupplyInfo] = useState<JobSupplyInfo | null>(null);
  // Pools gate (GRAVY-10349): the pools list endpoint decides whether the
  // worker-row "Add worker to pool" action exists — server-driven (P13) and
  // never for customer admins (P10). The GRAVY-10265 F9 "Preferred Workers"
  // tab this also used to gate was removed per GRAVY-10354.
  const [poolsEnabled, setPoolsEnabled] = useState(false);
  const [pools, setPools] = useState<PoolRow[]>([]);
  const phoneOnly = useNullableMediaQuery('(max-width: 559px)', null);
  const navigate = useNavigate();

  const { logEvent } = useAnalytics();

  const fetchQualityGate = useCallback(() => {
    if (jobId) {
      JobsAPI.getQualityGateData(jobId)
        .then(setQualityGate)
        .catch(() => {});
      JobsAPI.getQualityGateActiveJobIds([Number(jobId)])
        .then(({ supplyInfo: info }) => {
          setSupplyInfo(info[Number(jobId)] ?? null);
        })
        .catch(() => {});
    }
  }, [jobId]);

  useEffect(() => {
    logEvent(GAEvent.ViewJobDetails, jobId);
  }, []);

  useEffect(() => {
    fetchQualityGate();
  }, [fetchQualityGate]);

  const jobCustomerId = query?.data?.job?.account?.customer?.id;
  const jobSkillId = query?.data?.job?.skill?.id;

  useEffect(() => {
    if (!jobCustomerId || currentAdminIsCustomerAdmin) return;
    PreferredPoolsAPI.listPools(jobCustomerId)
      .then((result) => {
        setPoolsEnabled(result.enabled);
        setPools(result.pools);
      })
      .catch(() => {
        setPoolsEnabled(false);
        setPools([]);
      });
  }, [jobCustomerId, currentAdminIsCustomerAdmin]);

  // GRAVY-10349: the worker-row "Add worker to pool" action targets the
  // customer's active pool for the job's skill; no pool means no menu item.
  const jobSkillPool =
    (poolsEnabled &&
      !currentAdminIsCustomerAdmin &&
      jobSkillId &&
      pools.find(
        (pool) =>
          pool.status === 'active' &&
          String(pool.skill.id) === String(jobSkillId),
      )) ||
    null;

  const job = query?.data?.job || {};

  // Read off `query.data` rather than the `job` local: `job` falls back to `{}`
  // above, which erases the type — every `job.shifts[0]` in this file carries a
  // TS2339 for that reason, and there is no need to add more.
  const jobShifts = query?.data?.job?.shifts ?? [];

  // Validated against the job's OWN shifts, never trusted from the URL: an id
  // from another job (or a typo) must fall back to the historical behaviour
  // rather than blanking every worker row.
  const focusedShift =
    (requestedShiftId &&
      jobShifts.find(
        (shift) => String(shift.id) === String(requestedShiftId),
      )) ||
    null;

  const [showCancelJobModal, hideCancelJobModal] = useModal(
    () => (
      <CancellationDissmissModal
        context={ContextCancelRemoveModal.CancelJob}
        hideModal={() => {
          hideCancelJobModal();
          hideConfirmCancelJob();
        }}
        job={job}
      />
    ),
    [job],
  );

  const [confirmCancelJob, hideConfirmCancelJob] = useModal(
    () => (
      <ConfirmModalCancellation
        context={ContextConfirmModalCancellation.CancelJob}
        hideModal={hideConfirmCancelJob}
        onAction={() => {
          showCancelJobModal({ job });
        }}
      />
    ),
    [job],
  );

  const onCancelPress = (job: GetJobQuery['job']) => {
    const firstShiftStartAt = parseISO(job.firstShiftStartAt);

    const differenceHours = differenceInHours(firstShiftStartAt, new Date());

    if (differenceHours < DAY_HOURS && currentAdminIsCustomerAdmin) {
      return confirmCancelJob({ job });
    }

    return showCancelJobModal({ job });
  };

  const [jobPublish] = usePublishJobMutation({
    update: (cache) => {
      cache.modify({
        id: cache.identify(job),
        fields: { job() {} },
      });
    },
  });
  const [jobUnpublish] = useUnPublishJobMutation({
    update: (cache) => {
      cache.modify({
        id: cache.identify(job),
        fields: { job() {} },
      });
    },
  });

  const togglePublish = async () => {
    const value = jobPublished;
    try {
      if (value) {
        return await jobUnpublish({ variables: { jobId } });
      }

      return await jobPublish({ variables: { jobId } });
    } catch (err) {
      return err;
    }
  };

  // GRAVY-10809. `!query.data` alone held the skeleton FOREVER on any failure —
  // Apollo leaves `data` undefined, so this branch could not tell "still
  // loading" from "will never load", and rendered grey placeholder bars with no
  // message and no way out. Ops reached it from the Location Flag modal's
  // "Review shift" and had nothing to act on.
  //
  // `error` is read before `loading` deliberately: `pollInterval` is set on this
  // query, so a failing poll keeps `loading` cycling true — gating on `loading`
  // would leave the same dead end for the whole polling lifetime.
  if (query.error && !query.data) {
    return (
      <Page title="Shift">
        <div data-testid="job-load-error" style={{ padding: '20px' }}>
          <Alert
            description="Something went wrong loading this shift. Check your connection and try again."
            icon={faTriangleExclamation}
            status="danger"
            title="Couldn't load this shift"
          />
          <Button
            a11yLabel="Retry loading this shift"
            appearance="outline"
            label="Try again"
            onClick={() => {
              query.refetch();
            }}
          />
        </div>
      </Page>
    );
  }

  if (!query.data) {
    return <PageSkeleton />;
  }

  const jobStatus = getJobStatusByJob(job);
  const jobPublished = job.published;
  const jobWorkersCount = getHiredWorkersByJobWorkers(job.jobWorkers);
  const TABS: TabDefinition[] = [
    {
      a11yLabel: 'Workers',
      label: 'Workers',
      id: `${jobWorkersCount.length}/${job.quantity}`,
    },
    { a11yLabel: 'Shift Details', label: 'Shift Details' },
  ];

  /*
   * GRAVY-10877 — the shift's Activity log, as a tab (Figma 38490:69618).
   *
   * Tenant Admins only: it reads `/dashboard_v2/shift_activity`, which the
   * whole Dashboard V2 namespace restricts to them (`require_tenant_admin`), so
   * a client admin would get a tab that can only ever 403.
   *
   * One occurrence at a time, like the rest of this page: the `?shift=` the
   * page was opened for, else the first. The endpoint is per shift, and a
   * multi-day job's days have separate histories — merging them would put
   * Tuesday's hire under Monday's "No workers hired yet".
   */
  const agencyId = currentAgency?.id ? Number(currentAgency.id) : null;
  const activityShift = focusedShift ?? jobShifts[0] ?? null;
  const showActivity =
    !currentAdminIsCustomerAdmin && agencyId != null && activityShift != null;
  if (showActivity) {
    TABS.push({ a11yLabel: 'Activity', label: 'Activity' });
  }
  const ACTIVITY_TAB = 2;

  const getJobStatusLabel = () => {
    const startAtIso = parseISO(job.shifts[0].startAt);
    if (isWithin24Hours(startAtIso)) {
      if (!jobPublished && jobStatus === JobStatus.CANCELLED) {
        return [JobStatus.UNPUBLISHED, jobStatus];
      } else if (!jobPublished) {
        return JobStatus.UNPUBLISHED;
      } else if (jobStatus === JobStatus.CANCELLED) {
        return jobStatus;
      } else {
        return [];
      }
    } else if (!jobPublished) {
      return [JobStatus.UNPUBLISHED, jobStatus];
    } else {
      return jobStatus;
    }
  };

  const displayLabels = () => {
    const LABELS = [
      <JobBadge key={'status'} jobStatus={getJobStatusLabel()} />,
    ];
    if (job.jobType === JobTypeEnum.LTA) {
      LABELS.push(
        <Badge
          key={'lta'}
          css={{ marginRight: '10px' }}
          label="Long-term"
          status={'themeDefault'}
        />,
      );
    }

    if (
      !currentAdminIsCustomerAdmin &&
      qualityGate.customer_quality_gate_enabled &&
      !qualityGate.quality_gate_disabled
    ) {
      LABELS.push(
        <span
          key={'quality-gate'}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            backgroundColor: '#E7F4E680',
            borderRadius: '100px',
            padding: '4px 8px',
            marginRight: '10px',
            fontFamily: 'Manrope',
            fontWeight: 600,
            fontSize: '14px',
            lineHeight: '140%',
            color: '#44A735',
            whiteSpace: 'nowrap',
          }}
        >
          {supplyInfo?.low_supply
            ? 'Dependability Gate: Relaxed'
            : 'Dependability Gate: Strict'}
        </span>,
      );
    }

    return LABELS;
  };

  const workerDetails = () => {
    return (
      <Stack
        vertical
        gap={10}
        style={phoneOnly ? { padding: '0px' } : { padding: '0px 20px' }}
      >
        <WorkerTable
          job={job}
          jobStatus={jobStatus}
          refetchJob={() => query.refetch()}
          qualityGateActive={
            qualityGate.customer_quality_gate_enabled &&
            !qualityGate.quality_gate_disabled
          }
          skillPool={jobSkillPool}
          focusedShiftId={focusedShift?.id ?? null}
          focusedWorkerId={requestedWorkerId}
        />
      </Stack>
    );
  };
  const jobDetails = () => {
    return (
      <JobDetailsWrapper>
        <DetailsCard
          job={job}
          refetchJob={() => query.refetch()}
          qualityGate={qualityGate}
          onQualityGateUpdated={fetchQualityGate}
        />
      </JobDetailsWrapper>
    );
  };

  const chatIntercomIcon = useFeatureValue(
    FEATURE_TOGGLE.ChatIntercomIcon,
    false,
  );
  const ChatIntercomIconIOS = useFeatureValue(
    FEATURE_TOGGLE.ChatIntercomIconIOS,
    false,
  );

  return (
    <Page
      noPadding
      contentVarient="true"
      headerPadding={phoneOnly}
      size={'full'}
    >
      <Stack
        style={
          phoneOnly
            ? { padding: '0' }
            : { padding: '0', margin: '0px !important' }
        }
      >
        <JobHeader
          displayStatus={displayLabels()}
          handleCloseClick={() => onCancelPress(job)}
          handlePublishClick={togglePublish}
          isJobPublished={jobPublished}
          job={job}
          jobStatus={jobStatus}
        />
      </Stack>

      {/* GRAVY-10443 — surge pay, between the header and the tabs as the
          design places it. Renders nothing unless the schedule has something
          to say about this shift. */}
      <div style={{ padding: phoneOnly ? '0 14px' : '0 24px' }}>
        <SurgeBanner
          canManage={currentAdminIsTenantAdminRole}
          hiredCount={job.hiredWorkersCount}
          jobId={job.id}
          quantity={job.quantity}
          onChanged={() => query.refetch()}
        />
      </div>

      <Stack vertical gap={0} style={{ position: 'relative' }}>
        <Tabs
          minWidth={phoneOnly ? '50%' : '200px'}
          selected={tab}
          showTabOnMobile={true}
          tabs={TABS}
          wrapperStyle={{
            marginLeft: phoneOnly ? '0px' : '38px',
            padding: '0px',
          }}
          onSelect={setTab}
        />
        {showActivity && tab === ACTIVITY_TAB && (
          <button
            type="button"
            className="jobact-sort"
            data-testid="jobActivitySort"
            aria-label={
              activityOrder === 'desc'
                ? 'Sort activity oldest first'
                : 'Sort activity newest first'
            }
            title={activityOrder === 'desc' ? 'Newest first' : 'Oldest first'}
            onClick={() =>
              setActivityOrder((order) => (order === 'desc' ? 'asc' : 'desc'))
            }
          >
            {activityOrder === 'desc' ? (
              <BarsArrowDownIcon aria-hidden="true" />
            ) : (
              <BarsArrowUpIcon aria-hidden="true" />
            )}
          </button>
        )}
      </Stack>
      <Stack
        vertical
        gap={24}
        style={phoneOnly ? { padding: '0 14px' } : { marginTop: '20px' }}
      >
        {/* GRAVY-10453. Only when the job HAS more than one occurrence — on a
            single-shift job there is nothing to disambiguate and this would be
            noise. Names the occurrence outright, because the whole point of the
            deep link is that ops should not have to work out which day they are
            looking at. */}
        {focusedShift && jobShifts.length > 1 && (
          <div style={{ width: '100%' }} data-testid="job-focused-shift-alert">
            <Alert
              description={`Hours below are for ${formatISO(
                focusedShift.startAt,
                "EEE, MMM do 'at' h:mmbbb",
              )}. This shift has ${jobShifts.length} occurrences.`}
              icon={faClock}
              status="neutral"
              title="Showing one occurrence"
            />
          </div>
        )}

        {job.postedAt &&
          !job.published &&
          jobStatus !== JobStatus.COMPLETED && (
            <div style={{ width: '100% ' }}>
              <Alert
                description={`This shift will be published to GravyWorkers on ${formatISO(
                  job.postedAt,
                  "EEE, MMM do 'at' h:mmbbb",
                )}`}
                icon={faClock}
                status="neutral"
                title="Shift scheduled to be posted"
              />
            </div>
          )}

        <div style={{ width: '100%' }}>
          {tab === 0 && workerDetails()}
          {tab === 1 && jobDetails()}
          {showActivity && tab === ACTIVITY_TAB && (
            <JobActivityTab
              agencyId={agencyId}
              occurrenceLabel={
                jobShifts.length > 1
                  ? `Activity for ${formatISO(
                      activityShift.startAt,
                      "EEE, MMM do 'at' h:mmbbb",
                    )} — one of ${jobShifts.length} occurrences.`
                  : null
              }
              order={activityOrder}
              shiftId={Number(activityShift.id)}
            />
          )}
        </div>
      </Stack>
      {chatIntercomIcon && (!isIOS() || (isIOS() && ChatIntercomIconIOS)) && (
        <AgencyChat />
      )}
    </Page>
  );
};

export default JobRevamp;
